import React, { useCallback, useEffect, useMemo, useState } from "react";
import { schoolLeaderboard } from "../../lib/aggregateAnalytics";
import { fetchLeanAnalytics } from "../../lib/fetchRegistrationAnalytics";
import { loadAnalyticsConfig } from "../../lib/schoolAliasesFirestore";
import {
  buildAliasLookup,
  applySchoolAliases,
  filterHiddenRecords,
} from "../../lib/schoolAliases";
import { buildOutreachRows, normalizeWhatsApp, whatsAppUrl } from "../../lib/schoolOutreach";
import {
  loadOutreachTracking,
  claimSchoolForEmail,
  updateSchoolTrackingForEmail,
  updateQuizConfirmationForSchool,
  getSchoolTracking,
  countLockedSchoolsForEmail,
  CALL_STATUS_OPTIONS,
  OutreachClaimError,
} from "../../lib/outreachTrackingFirestore";
import {
  MAX_SCHOOLS_PER_USER,
  getOutreachUserName,
} from "../../lib/outreachMvpCredentials";
import {
  summarizeQuizBySchool,
  enrichRowQuizMeta,
  isQuizConfirmed,
} from "../../lib/quizSeason";
import { archiveStaleQuizForSchool } from "../../lib/quizArchiveFirestore";
import QuizSeasonCell from "./QuizSeasonCell";

const MEDAL = ["🥇", "🥈", "🥉"];
const PILLARS = ["Armitage", "Insignia", "Zenith", "Orthodox"];

function IctTick({ registered }) {
  if (!registered) return <span className="lb-none">—</span>;
  return <span className="ict-badge">✓</span>;
}

function ClaimCell({
  tracking,
  userEmail,
  isAdmin,
  myClaimCount,
  onClaim,
  claiming,
}) {
  const { lockedByEmail } = tracking;

  if (!lockedByEmail) {
    const atLimit = myClaimCount >= MAX_SCHOOLS_PER_USER;
    return (
      <button
        type="button"
        className="outreach-claim-btn"
        disabled={atLimit || claiming}
        onClick={onClaim}
        title={
          atLimit
            ? `You have reached the ${MAX_SCHOOLS_PER_USER}-school limit`
            : "Claim this school for outreach"
        }
      >
        {claiming ? "…" : atLimit ? "Limit reached" : "Claim"}
      </button>
    );
  }

  if (lockedByEmail === userEmail) {
    return <span className="outreach-lock-badge mine">Locked to you</span>;
  }

  const ownerName = getOutreachUserName(lockedByEmail);
  return (
    <span className="outreach-lock-badge taken" title={lockedByEmail}>
      {isAdmin ? ownerName : "Taken"}
    </span>
  );
}

export default function OutreachDashboard({
  userEmail = "",
  userName = "",
  isAdmin = false,
}) {
  const [records, setRecords] = useState([]);
  const [aliasGroups, setAliasGroups] = useState([]);
  const [hiddenSchools, setHiddenSchools] = useState([]);
  const [schoolContacts, setSchoolContacts] = useState([]);
  const [manualSchools, setManualSchools] = useState([]);
  const [schoolState, setSchoolState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [claimingKey, setClaimingKey] = useState("");
  const [confirmingQuizKey, setConfirmingQuizKey] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [mySchoolsOnly, setMySchoolsOnly] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);

  const reloadTracking = useCallback(async () => {
    const tracking = await loadOutreachTracking();
    setSchoolState(tracking.schoolState);
    return tracking.schoolState;
  }, []);

  const reloadAnalytics = useCallback(async () => {
    const [{ records: recs }, tracking] = await Promise.all([
      fetchLeanAnalytics(),
      loadOutreachTracking(),
    ]);
    setRecords(recs);
    setSchoolState(tracking.schoolState);
    return { records: recs, schoolState: tracking.schoolState };
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ records: recs }, config, tracking] = await Promise.all([
          fetchLeanAnalytics(),
          loadAnalyticsConfig(),
          loadOutreachTracking(),
        ]);
        let recordsToUse = recs;
        const lookup = buildAliasLookup(config.groups);
        const visible = filterHiddenRecords(
          applySchoolAliases(recs, lookup),
          config.hiddenSchools
        );
        const { stale } = summarizeQuizBySchool(visible);
        let reconciled = false;

        for (const key of Object.keys(tracking.schoolState)) {
          if (!isQuizConfirmed(tracking.schoolState[key])) continue;
          if ((stale[key] ?? 0) === 0) continue;
          await archiveStaleQuizForSchool(key, config.groups);
          reconciled = true;
        }

        if (reconciled) {
          const fresh = await fetchLeanAnalytics();
          recordsToUse = fresh.records;
        }

        setRecords(recordsToUse);
        setAliasGroups(config.groups);
        setHiddenSchools(config.hiddenSchools);
        setSchoolContacts(config.schoolContacts);
        setManualSchools(config.manualSchools);
        setSchoolState(tracking.schoolState);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const lookup = useMemo(() => buildAliasLookup(aliasGroups), [aliasGroups]);
  const visibleRecords = useMemo(
    () => filterHiddenRecords(applySchoolAliases(records, lookup), hiddenSchools),
    [records, lookup, hiddenSchools]
  );

  const baseRows = useMemo(() => schoolLeaderboard(visibleRecords), [visibleRecords]);

  const { current: quizCurrentBySchool, stale: quizStaleBySchool } = useMemo(
    () => summarizeQuizBySchool(visibleRecords),
    [visibleRecords]
  );

  const ictBySchool = useMemo(() => {
    const s = new Set();
    for (const r of visibleRecords) {
      if (r.collection !== "ict_society_registrations") continue;
      const k =
        (r.schoolKey ?? (r.schoolName ?? "").trim().toLowerCase()) || "(unknown)";
      s.add(k);
    }
    return s;
  }, [visibleRecords]);

  const rowsWithMeta = useMemo(
    () =>
      baseRows.map((row) => ({
        ...row,
        ...enrichRowQuizMeta(
          row.key,
          schoolState,
          quizCurrentBySchool,
          quizStaleBySchool
        ),
        hasIct: ictBySchool.has(row.key),
      })),
    [baseRows, quizCurrentBySchool, quizStaleBySchool, ictBySchool, schoolState]
  );

  const schools = useMemo(
    () => buildOutreachRows(rowsWithMeta, manualSchools, schoolContacts),
    [rowsWithMeta, manualSchools, schoolContacts]
  );

  const myClaimCount = useMemo(
    () => countLockedSchoolsForEmail(schoolState, userEmail),
    [schoolState, userEmail]
  );

  const canEditSchool = useCallback(
    (tracking) =>
      isAdmin ||
      (tracking.lockedByEmail && tracking.lockedByEmail === userEmail),
    [isAdmin, userEmail]
  );

  const handleClaim = useCallback(
    async (schoolKey) => {
      setClaimingKey(schoolKey);
      setSaving(true);
      try {
        const { schoolState: next } = await claimSchoolForEmail({
          schoolKey,
          userEmail,
        });
        setSchoolState(next);
      } catch (err) {
        console.error(err);
        await reloadTracking();
        alert(
          err instanceof OutreachClaimError
            ? err.message
            : "Failed to claim school. It may have been taken."
        );
      } finally {
        setClaimingKey("");
        setSaving(false);
      }
    },
    [userEmail, reloadTracking]
  );

  const handleQuizConfirm = useCallback(
    async (schoolKey, { confirmType, returningTeams }) => {
      setConfirmingQuizKey(schoolKey);
      setSaving(true);
      try {
        await updateQuizConfirmationForSchool({
          schoolKey,
          userEmail,
          confirmType,
          returningTeams,
          aliasGroups,
        });
        await reloadAnalytics();
      } catch (err) {
        console.error(err);
        await reloadAnalytics();
        alert(
          err instanceof OutreachClaimError
            ? err.message
            : "Failed to save quiz confirmation."
        );
      } finally {
        setConfirmingQuizKey("");
        setSaving(false);
      }
    },
    [userEmail, aliasGroups, reloadAnalytics]
  );

  const handleUpdate = useCallback(
    async (schoolKey, patch) => {
      setSaving(true);
      try {
        const { schoolState: next } = await updateSchoolTrackingForEmail({
          schoolKey,
          userEmail,
          isAdmin,
          patch,
        });
        setSchoolState(next);
      } catch (err) {
        console.error(err);
        await reloadTracking();
        alert(
          err instanceof OutreachClaimError
            ? err.message
            : "Failed to save outreach update."
        );
      } finally {
        setSaving(false);
      }
    },
    [userEmail, isAdmin, reloadTracking]
  );

  const filteredSchools = useMemo(() => {
    let list = schools;

    if (filterMode === "available") {
      list = list.filter(
        (s) => !getSchoolTracking(schoolState, s.key).lockedByEmail
      );
    } else if (filterMode === "claimed") {
      list = list.filter(
        (s) => !!getSchoolTracking(schoolState, s.key).lockedByEmail
      );
    }

    if (filterMode === "quiz-stale") {
      list = list.filter((s) => s.quizPending);
    }

    if (mySchoolsOnly) {
      list = list.filter(
        (s) =>
          getSchoolTracking(schoolState, s.key).lockedByEmail === userEmail
      );
    }

    return list;
  }, [schools, schoolState, filterMode, mySchoolsOnly, userEmail]);

  const stats = useMemo(() => {
    let called = 0;
    let messaged = 0;
    let claimed = 0;
    let quizPending = 0;
    for (const s of schools) {
      const t = getSchoolTracking(schoolState, s.key);
      if (t.lockedByEmail) claimed++;
      if (t.callStatus && t.callStatus !== "not_called") called++;
      if (t.messageSent) messaged++;
      if (s.quizPending) quizPending++;
    }
    return { called, messaged, claimed, quizPending, total: schools.length };
  }, [schools, schoolState]);

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="analytics-spinner" />
        <p>Loading outreach data…</p>
      </div>
    );
  }

  return (
    <div className="outreach-dashboard">
      {stats.quizPending > 0 && (
        <div className="outreach-quiz-banner" role="status">
          <strong>{stats.quizPending}</strong> school{stats.quizPending !== 1 ? "s" : ""}{" "}
          have old quiz registrations — tap <strong>old</strong> in the Quiz column to
          update.
        </div>
      )}

      <div className="outreach-toolbar">
        <div className="outreach-stat-chips">
          <span className="outreach-stat-chip">{stats.total} schools</span>
          <span className="outreach-stat-chip">{stats.claimed} claimed</span>
          {stats.quizPending > 0 && (
            <span className="outreach-stat-chip outreach-stat-warn">
              {stats.quizPending} quiz confirm
            </span>
          )}
          <span className="outreach-stat-chip">{stats.called} called</span>
          <span className="outreach-stat-chip">{stats.messaged} messaged</span>
          <span className="outreach-stat-chip outreach-my-count">
            Your schools: {myClaimCount}/{MAX_SCHOOLS_PER_USER}
          </span>
          {saving && <span className="outreach-saving">Saving…</span>}
        </div>

        <div className="outreach-toolbar-row">
          <select
            className="analytics-select"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="all">All schools</option>
            <option value="available">Available only</option>
            <option value="claimed">Claimed only</option>
            <option value="quiz-stale">Old quiz teams</option>
          </select>

          <label className="outreach-check-label">
            <input
              type="checkbox"
              checked={mySchoolsOnly}
              onChange={(e) => setMySchoolsOnly(e.target.checked)}
            />
            My schools only
          </label>
        </div>
      </div>

      <div className="leaderboard-table-wrapper outreach-table-wrap">
        <table className="analytics-table outreach-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>School</th>
              <th>%</th>
              <th className="col-narrow">Arm.</th>
              <th className="col-narrow">Ins.</th>
              <th className="col-narrow">Zen.</th>
              <th className="col-narrow">Ort.</th>
              <th>Quiz</th>
              <th>ICT</th>
              <th>Claim</th>
              <th>Call</th>
              <th>Message</th>
              <th>Contact Details</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {filteredSchools.map((row) => {
              const tracking = getSchoolTracking(schoolState, row.key);
              const editable = canEditSchool(tracking);
              const contactNum = tracking.contactPhone || row.whatsapp;
              const contactRole = tracking.contactRole || "TIC";
              const notesVal = tracking.notes || "";
              const wa = normalizeWhatsApp(contactNum);
              const waLink = wa ? whatsAppUrl(wa, row.smartMessage) : "";
              const callChipClass =
                CALL_STATUS_OPTIONS.find((o) => o.id === tracking.callStatus)
                  ?.id ?? "not_called";

              return (
                <tr
                  key={row.key}
                  className={[
                    row.rank && row.rank <= 3 ? `top-${row.rank}` : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <td className="rank-cell">
                    {row.rank ? MEDAL[row.rank - 1] ?? row.rank : "—"}
                  </td>
                  <td className="school-name-cell">
                    {row.displayName}
                    <span className="outreach-reg-count">{row.count} regs</span>
                  </td>
                  <td>{row.pct}%</td>
                  <td className="col-narrow">
                    {row.byHostingSchool?.Armitage ?? 0}
                  </td>
                  <td className="col-narrow">
                    {row.byHostingSchool?.Insignia ?? 0}
                  </td>
                  <td className="col-narrow">
                    {row.byHostingSchool?.Zenith ?? 0}
                  </td>
                  <td className="col-narrow">
                    {row.byHostingSchool?.Orthodox ?? 0}
                  </td>
                  <td className="quiz-col">
                    <QuizSeasonCell
                      effectiveCount={row.quizTeams}
                      currentCount={row.quizCurrentTeams}
                      staleCount={row.quizStaleTeams}
                      pending={row.quizPending}
                      onConfirm={(payload) => handleQuizConfirm(row.key, payload)}
                      saving={confirmingQuizKey === row.key}
                      compact
                    />
                  </td>
                  <td>
                    <IctTick registered={row.hasIct} />
                  </td>
                  <td>
                    <ClaimCell
                      tracking={tracking}
                      userEmail={userEmail}
                      isAdmin={isAdmin}
                      myClaimCount={myClaimCount}
                      claiming={claimingKey === row.key}
                      onClaim={() => handleClaim(row.key)}
                    />
                  </td>
                  <td>
                    <select
                      className={`outreach-cell-select outreach-call-select call-${callChipClass}`}
                      value={tracking.callStatus}
                      disabled={!editable}
                      onChange={(e) =>
                        handleUpdate(row.key, { callStatus: e.target.value })
                      }
                    >
                      {CALL_STATUS_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`outreach-msg-btn ${
                        tracking.messageSent ? "sent" : ""
                      }`}
                      disabled={!editable}
                      onClick={() =>
                        handleUpdate(row.key, {
                          messageSent: !tracking.messageSent,
                        })
                      }
                    >
                      {tracking.messageSent ? "✓ Sent" : "Mark sent"}
                    </button>
                  </td>
                  <td>
                    {contactNum ? (
                      <div className="outreach-contact-col-cell">
                        <span className="outreach-contact-number" title={contactNum} style={{ fontSize: "12px", display: "block", whiteSpace: "nowrap" }}>
                          {contactNum} <span className="outreach-role-badge" style={{ background: "rgba(255,255,255,0.08)", padding: "1px 4px", borderRadius: "4px", fontSize: "10px", color: "#aaa" }}>({contactRole})</span>
                        </span>
                        <div className="outreach-contact-actions" style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                          <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lb-wa-btn"
                            title="Open WhatsApp"
                            style={{ padding: "2px 6px", fontSize: "11px" }}
                          >
                            WA
                          </a>
                          <button
                            type="button"
                            className="outreach-vcard-btn"
                            onClick={() => downloadVCard(row.displayName, contactNum, contactRole)}
                            title="Download Contact (vCard)"
                            style={{ padding: "2px 6px", fontSize: "11px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#ddd", borderRadius: "4px", cursor: "pointer" }}
                          >
                            📇 vCard
                          </button>
                          {editable && (
                            <button
                              type="button"
                              className="outreach-edit-details-btn"
                              onClick={() => setEditingSchool(row)}
                              title="Edit Contact details"
                              style={{ padding: "2px 6px", fontSize: "11px", background: "rgba(255, 77, 77, 0.15)", border: "1px solid rgba(255, 77, 77, 0.35)", color: "#ff8080", borderRadius: "4px", cursor: "pointer" }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="outreach-contact-col-cell">
                        {editable ? (
                          <button
                            type="button"
                            className="lb-contact-add"
                            onClick={() => setEditingSchool(row)}
                            style={{ padding: "3px 8px", fontSize: "11px", borderRadius: "4px", border: "1px dashed rgba(255,255,255,0.3)", background: "transparent", color: "#aaa", cursor: "pointer" }}
                          >
                            + Add number
                          </button>
                        ) : (
                          <span className="lb-none">—</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="outreach-comments-cell" style={{ maxWidth: "200px" }}>
                      {notesVal ? (
                        <span
                          className="outreach-comments-text"
                          title={notesVal}
                          onClick={() => editable && setEditingSchool(row)}
                          style={{ cursor: editable ? "pointer" : "default", fontSize: "12px", color: "#ccc", display: "inline-block", wordBreak: "break-word" }}
                        >
                          {notesVal.length > 40 ? `${notesVal.slice(0, 37)}...` : notesVal}
                        </span>
                      ) : (
                        editable ? (
                          <button
                            type="button"
                            className="outreach-add-comment-btn"
                            onClick={() => setEditingSchool(row)}
                            style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer", fontSize: "11px", padding: 0 }}
                          >
                            + Add comment
                          </button>
                        ) : (
                          <span className="lb-none">—</span>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="outreach-cards">
        {filteredSchools.map((row) => {
          const tracking = getSchoolTracking(schoolState, row.key);
          const editable = canEditSchool(tracking);
          const contactNum = tracking.contactPhone || row.whatsapp;
          const contactRole = tracking.contactRole || "TIC";
          const notesVal = tracking.notes || "";
          const wa = normalizeWhatsApp(contactNum);
          const waLink = wa ? whatsAppUrl(wa, row.smartMessage) : "";
          const callChipClass =
            CALL_STATUS_OPTIONS.find((o) => o.id === tracking.callStatus)?.id ??
            "not_called";

          return (
            <div
              key={row.key}
              className={[
                "outreach-card",
                row.rank && row.rank <= 3 ? `lb-top-${row.rank}` : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="outreach-card-head">
                <span className="lb-card-rank">
                  {row.rank ? MEDAL[row.rank - 1] ?? `#${row.rank}` : "—"}
                </span>
                <span className="lb-card-name">{row.displayName}</span>
                <span className="lb-card-count">{row.count}</span>
                <span className="lb-card-pct">{row.pct}%</span>
              </div>
              <div className="outreach-card-pillars">
                {PILLARS.map((p) => (
                  <span key={p} className="lb-pillar-chip">
                    <span className="lb-pillar-name">{p.slice(0, 3)}</span>
                    <span className="lb-pillar-val">
                      {row.byHostingSchool?.[p] ?? 0}
                    </span>
                  </span>
                ))}
              </div>
              <div className="lb-card-badges outreach-card-quiz-row">
                <QuizSeasonCell
                  effectiveCount={row.quizTeams}
                  currentCount={row.quizCurrentTeams}
                  staleCount={row.quizStaleTeams}
                  pending={row.quizPending}
                  onConfirm={(payload) => handleQuizConfirm(row.key, payload)}
                  saving={confirmingQuizKey === row.key}
                />
                <IctTick registered={row.hasIct} />
              </div>
              <div className="outreach-card-fields">
                <label className="filter-label">Claim</label>
                <ClaimCell
                  tracking={tracking}
                  userEmail={userEmail}
                  isAdmin={isAdmin}
                  myClaimCount={myClaimCount}
                  claiming={claimingKey === row.key}
                  onClaim={() => handleClaim(row.key)}
                />
                <label className="filter-label">Call</label>
                <select
                  className={`outreach-cell-select outreach-call-select call-${callChipClass}`}
                  value={tracking.callStatus}
                  disabled={!editable}
                  onChange={(e) =>
                    handleUpdate(row.key, { callStatus: e.target.value })
                  }
                >
                  {CALL_STATUS_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`outreach-msg-btn ${
                    tracking.messageSent ? "sent" : ""
                  }`}
                  disabled={!editable}
                  onClick={() =>
                    handleUpdate(row.key, {
                      messageSent: !tracking.messageSent,
                    })
                  }
                >
                  {tracking.messageSent ? "✓ Message sent" : "Mark message sent"}
                </button>
                {waLink && (
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="export-btn wa-open-btn"
                    style={{ textAlign: "center", marginTop: 6 }}
                  >
                    Open WhatsApp
                  </a>
                )}

                <label className="filter-label" style={{ marginTop: 8 }}>Contact Details</label>
                {contactNum ? (
                  <div className="outreach-card-contact-box" style={{ background: "rgba(255,255,255,0.03)", padding: "8px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <span className="outreach-card-contact-num" style={{ fontSize: "13px", display: "block" }}>
                      {contactNum} <strong style={{ color: "#ff8080" }}>({contactRole})</strong>
                    </span>
                    <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                      <button
                        type="button"
                        className="outreach-vcard-btn"
                        onClick={() => downloadVCard(row.displayName, contactNum, contactRole)}
                        style={{ flex: 1, padding: "5px", fontSize: "11px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#ddd", borderRadius: "4px", cursor: "pointer" }}
                      >
                        📇 Download Contact
                      </button>
                      {editable && (
                        <button
                          type="button"
                          className="outreach-edit-details-btn"
                          onClick={() => setEditingSchool(row)}
                          style={{ padding: "5px 10px", fontSize: "11px", background: "rgba(255, 77, 77, 0.15)", border: "1px solid rgba(255, 77, 77, 0.35)", color: "#ff8080", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    {editable ? (
                      <button
                        type="button"
                        className="lb-contact-add"
                        onClick={() => setEditingSchool(row)}
                        style={{ padding: "6px 12px", fontSize: "12px", width: "100%", borderRadius: "6px", border: "1px dashed rgba(255,255,255,0.3)", background: "transparent", color: "#aaa", cursor: "pointer" }}
                      >
                        + Add contact details
                      </button>
                    ) : (
                      <span className="lb-none">—</span>
                    )}
                  </div>
                )}

                <label className="filter-label" style={{ marginTop: 8 }}>Comments / Updates</label>
                <div className="outreach-card-comments" style={{ background: "rgba(255,255,255,0.03)", padding: "8px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.08)" }}>
                  {notesVal ? (
                    <p style={{ margin: 0, fontSize: "12px", color: "#ccc", whiteSpace: "pre-wrap", lineHeight: "1.4" }}>
                      {notesVal}
                    </p>
                  ) : (
                    <span className="lb-none" style={{ fontSize: "12px", color: "#666" }}>No comments yet</span>
                  )}
                  {editable && (
                    <button
                      type="button"
                      className="outreach-add-comment-btn"
                      onClick={() => setEditingSchool(row)}
                      style={{ background: "transparent", border: "none", color: "#ff8080", cursor: "pointer", fontSize: "11px", padding: 0, marginTop: "6px", display: "block", textAlign: "left" }}
                    >
                      {notesVal ? "✏️ Edit comments" : "+ Add comment"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editingSchool && (
        <OutreachContactModal
          school={editingSchool}
          tracking={getSchoolTracking(schoolState, editingSchool.key)}
          saving={saving}
          onClose={() => setEditingSchool(null)}
          onSave={(patch) => {
            handleUpdate(editingSchool.key, patch);
            setEditingSchool(null);
          }}
        />
      )}
    </div>
  );
}

function downloadVCard(schoolName, phoneNumber, role) {
  if (!phoneNumber) return;
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, ""); // keep + and digits
  const formattedRole = role === "Chairman" ? "Chairman" : "TIC";
  const contactName = `BTUI 26_${schoolName}_${formattedRole}`;
  
  const vcard = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${contactName}`,
    `N:${formattedRole};BTUI 26_${schoolName};;;`,
    `TEL;TYPE=CELL,VOICE:${cleanPhone}`,
    "END:VCARD"
  ].join("\r\n");

  const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${contactName.replace(/[^a-zA-Z0-9]/g, "_")}.vcf`;
  a.click();
  URL.revokeObjectURL(url);
}

function OutreachContactModal({
  school,
  tracking,
  onSave,
  onClose,
  saving,
}) {
  const [whatsapp, setWhatsapp] = useState("");
  const [contactRole, setContactRole] = useState("TIC");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!school) return;
    setWhatsapp(tracking.contactPhone || school.whatsapp || "");
    setContactRole(tracking.contactRole || "TIC");
    setNotes(tracking.notes || "");
  }, [school, tracking]);

  if (!school) return null;

  const previewMsg = school.smartMessage || "";
  const currentPhone = whatsapp.trim();

  const handleSave = () => {
    onSave({
      contactPhone: whatsapp.trim(),
      contactRole,
      notes: notes.trim(),
    });
  };

  return (
    <div className="school-contact-overlay" onClick={onClose}>
      <div className="school-contact-modal" onClick={(e) => e.stopPropagation()}>
        <header className="school-contact-header">
          <h3>{school.displayName}</h3>
          <button type="button" className="control-close-btn" onClick={onClose} style={{ background: "transparent", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer" }}>
            ×
          </button>
        </header>

        <div className="school-contact-stats">
          <span>{school.count} reg{school.count !== 1 ? "s" : ""}</span>
          <span>Score {school.engagementScore}</span>
          <span className={`outreach-pill ${school.outreach?.className}`}>
            {school.outreach?.label}
          </span>
        </div>

        {school.gaps?.length > 0 && (
          <p className="school-contact-gaps" style={{ margin: "4px 0 12px", fontSize: "12px", color: "#eab308" }}>
            Gaps: {school.gaps.join(" · ")}
          </p>
        )}

        <div style={{ marginBottom: "12px" }}>
          <label className="filter-label" style={{ display: "block", marginBottom: "4px", fontSize: "11px", color: "#aaa" }}>WhatsApp number</label>
          <input
            className="control-input"
            placeholder="e.g. 077 123 4567 or 94771234567"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            inputMode="tel"
            style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "#fff", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: "12px" }}>
          <label className="filter-label" style={{ display: "block", marginBottom: "4px", fontSize: "11px", color: "#aaa" }}>Contact Role</label>
          <select
            className="analytics-select"
            value={contactRole}
            onChange={(e) => setContactRole(e.target.value)}
            style={{ width: "100%", padding: "8px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "#fff", boxSizing: "border-box" }}
          >
            <option value="TIC" style={{ background: "#12121f", color: "#fff" }}>Teacher in Charge (TIC)</option>
            <option value="Chairman" style={{ background: "#12121f", color: "#fff" }}>Chairman / President</option>
          </select>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label className="filter-label" style={{ display: "block", marginBottom: "4px", fontSize: "11px", color: "#aaa" }}>Comments & Updates (Comment Box)</label>
          <textarea
            className="control-input control-textarea"
            placeholder="e.g. Called them today. TIC said they will register 2 teams next week."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "#fff", boxSizing: "border-box" }}
          />
        </div>

        {currentPhone && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            <button
              type="button"
              className="outreach-vcard-btn"
              onClick={() => downloadVCard(school.displayName, currentPhone, contactRole)}
              style={{ flex: 1, padding: "8px", background: "rgba(255, 77, 77, 0.15)", border: "1px solid #ff4d4d", borderRadius: "6px", color: "#ff8080", cursor: "pointer", fontWeight: "600" }}
            >
              📇 Download Contact (vCard)
            </button>
            <a
              href={whatsAppUrl(currentPhone, previewMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="lb-wa-btn"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "8px 16px", background: "rgba(34, 197, 94, 0.15)", border: "1px solid #22c55e", borderRadius: "6px", color: "#22c55e", textDecoration: "none", fontWeight: "600" }}
            >
              WA
            </a>
          </div>
        )}

        {previewMsg && (
          <div className="smart-message-preview">
            <span className="filter-label">Smart message preview</span>
            <p>{previewMsg}</p>
          </div>
        )}

        <div className="school-contact-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
          <button
            type="button"
            className="outreach-logout-btn"
            onClick={onClose}
            style={{ padding: "8px 16px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="outreach-claim-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ padding: "8px 20px" }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
